import { useContext, useEffect, useRef, useState } from "react";
import styles from "./styles/PosSummary.module.css";
import POSDataContext from "../../../context/POSDataContext";
import { axiosSW } from "../../../api/axiosDefaults";
import DataContext from "../../../context/DataContext";
import AuthContext from "../../../context/AuthContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Spinner from "../../../components/ui/spinner/Spinner";
import PosSummaryPanel from "./PosSummaryPanel";
import PosTransactionsPanel from "./PosTransactionsPanel";
import PosSummaryModal from "./PosSummaryModal";
import InfoModal from "../../../components/info_modal/InfoModal";
import Button from "../../../components/ui/button/Button";

import { useTranslation } from "react-i18next";
/**
 * The POS summary page
 * The upper parts shows session related information and  the ;ower part shows the transactions
 * the over all process is as follows:
 * 1- When clicking on the X button in a transaction tab the showModal state will be set to true,
 *    and the PosSummaryModal will be shown
 * 2- The PosSummaryModal will fetch the transaction details and show them in a modal window
 * 3- clicking the confirm cancelation button in the PosSummaryModal will initiate the cancelation process
 * 4- An authintication view in the PosSummaryModall will be shown to enter credentials
 * 5- if the authintaication fails, a message will be shown in the PosSummaryModal
 * 6- if the authintaication is successful, a confirmation modal
 *    (the InfoModal) will appear to confirm the cancelation
 * 7- clicking the confirm button in the InfoModal will initiate the cancelation process
 * 8- if the cancellation is successful a  confiramation message will be shown in the InfoModal
 */
const PosSummary = () => {
    const {
        setIsLoadingPos, // {Function} - Function to set the loading state in POS
        handleErrorPos, // {Function} - Function to handle errors in POS
        fetchSummary, // {Function} - Function to fetch the summary data
        setPayments, // {Function} - Function to set the payments
        setCount, // {Function} - Function to set the count of items in the shopping cart
        setFilterExpression, // {Function} - Function to set the filter expression
        setSummaryPos, // {Function} - Function to set the summary data in POS
        isLoadingPos, // {Boolean} - Indicates if the data is loading in POS
        userLogout,
    } = useContext(POSDataContext);
    const {
        isServiceDataAvailable, // {Boolean} - Indicates if the service data is available
        serviceData, // {Function} - Fetch service data
        currentBU, // {String} - The current business user
        homePath, // {String} - The path to the home page
        connection, // {Object} - The client's settings
        isLoading, // {Boolean} - Indicates if the data is loading
        JWTToken,
    } = useContext(DataContext);

    const {
        user, // {Object} - The authenticated user's data
        logout, // {Function} - Function to log out the current user
    } = useContext(AuthContext);

    const { t } = useTranslation();
    const { bUser } = useParams(); // Extracts 'bUser' parameter from the URL.
    const [notReadyView, setNotReadyView] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showCloseSessionConfirmation, setShowCloseSessionConfirmation] =
        useState(false);
    const [infoMessage, setInfoMessage] = useState("");
    const [currentTransaction, setCurrentTransaction] = useState(null);

    const navigate = useNavigate(); // Hook for navigation in React Router.

    const location = useLocation();
    const closeSessionButton = useRef(null);

    useEffect(() => {
        if (isServiceDataAvailable && (!isLoadingPos || !isLoading)) {
            const timer = setTimeout(() => {
                setNotReadyView(false);
            }, 50);

            // Cleanup function
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isServiceDataAvailable, isLoadingPos, isLoading]);

    // Redirect to home if no Busness user
    useEffect(() => {
        if (!currentBU) {
            navigate(`/${bUser}/pos`);
        } else {
            JWTToken && fetchSummary();
        }
    }, [bUser, currentBU, JWTToken]);

    // Fetch service data
    useEffect(() => {
        bUser &&
            JWTToken &&
            (!isServiceDataAvailable || bUser !== currentBU) &&
            serviceData(bUser);
    }, [bUser, isServiceDataAvailable, JWTToken]);

    // Scroll to the close session button when redirected to it
    useEffect(() => {
        if (location.state?.scrollTo) {
            const target = location.state.scrollTo;
            setTimeout(() => {
                if (target === "closeSession" && closeSessionButton.current) {
                    closeSessionButton.current.scrollIntoView({
                        behavior: "smooth",
                    });
                }
            }, 750);
        }
    }, [location, isLoadingPos]);

    /**
     * Close session handler
     * it will show the confirmation modal
     */
    const handleCloseSession = () => {
        setInfoMessage(`${t("close session")}: ${user?.cashierSessionID}`);
        setShowCloseSessionConfirmation(true);
    };

    /**
     * Close the current session
     * will open a pdf report in new tab with the print dialog
     */
    const closeSession = async () => {
        const payLoad = {
            uniqueIdentifier: connection?.uniqueIdentifier,
            cashierSessionID: user?.cashierSessionID,
        };

        try {
            setIsLoadingPos(true);

            const { data } = await axiosSW.post(
                "Authentication/pos/CloseCashierSession",
                payLoad,
            );

            if (data && data?.statusOk) {
                // Open pdf in a new tab with print dialog
                if (data) {
                    const base64PDF = data.fileContent;
                    const binaryString = window.atob(base64PDF);
                    const len = binaryString.length;
                    const bytes = new Uint8Array(len);
                    for (let i = 0; i < len; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    const blob = new Blob([bytes], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);

                    // Open the PDF in a new tab
                    const pdfWindow = window.open(url, "_blank");
                    if (pdfWindow) {
                        // Trigger the print dialog after the PDF is loaded
                        pdfWindow.addEventListener("load", () => {
                            pdfWindow.print();
                        });
                    }

                    setIsLoadingPos(false);
                    // Reset to defaults
                    setPayments([]);
                    setFilterExpression(null);
                    setCurrentTransaction(null);
                    setCount(0);

                    setSummaryPos(null);
                    logout(user?.userName);
                    navigate(homePath);
                }
            }
        } catch (error) {
            if (error?.request?.status === 404) {
                setIsLoadingPos(false);
                userLogout();
            } else {
                setIsLoadingPos(false);

                handleErrorPos(error);
            }
        }
    };

    return (
        <article
            className={`${styles.main_page_wrapper} main-page-container flex-container`}
        >
            {/* Show spinner when fitching service data only */}
            {!isServiceDataAvailable ? (
                <Spinner />
            ) : showModal ? (
                // Show the info modal for the Transaction to be canceled
                <PosSummaryModal
                    currentTransaction={currentTransaction}
                    setShowModal={setShowModal}
                    setShowInfoModal={setShowInfoModal}
                    setInfoMessage={setInfoMessage}
                />
            ) : showInfoModal ? (
                // Show the confirmation modal for the Transaction to be canceled
                <InfoModal
                    setShowInfoModal={setShowInfoModal}
                    confirmFunction={null}
                    cancelCloseFunction={null}
                    type={"confirm"}
                    message={infoMessage}
                    cancelCloseButton={"close"}
                    inProcess={false}
                />
            ) : showCloseSessionConfirmation ? (
                // show the close session confirmation modal
                <InfoModal
                    setShowInfoModal={setShowCloseSessionConfirmation}
                    confirmFunction={closeSession}
                    cancelCloseFunction={null}
                    type={"confirm"}
                    message={infoMessage}
                    cancelCloseButton={"cancel"}
                    inProcess={false}
                    buttonDisable={isLoadingPos}
                />
            ) : (
                <main
                    className={`${
                        styles.main_summary_container
                    }  transition-container
                    ${
                        notReadyView
                            ? "transition-hidden"
                            : "transition-visible"
                    }  
                    main-container v-flex-container`}
                >
                    {/* The summary staistics panel */}
                    <PosSummaryPanel />
                    {/* The transactions panle */}
                    <PosTransactionsPanel
                        setShowModal={setShowModal}
                        setCurrentTransaction={setCurrentTransaction}
                    />
                    <div
                        className={`${styles.summary_button_container} flex-container`}
                        ref={closeSessionButton}
                    >
                        <Button
                            body={t("close session")}
                            type={"button"}
                            size={"adapt"}
                            onClickFuction={handleCloseSession}
                            // disabled={}
                            // disableHTML={  }
                        />
                    </div>
                </main>
            )}
        </article>
    );
};

export default PosSummary;
